// Storage service now using SQLite via storageAdapter (migrated from IndexedDB)
import store from "@/store/index";
import data from "bootstrap/js/src/dom/data";
import axios from "axios";
import { responses } from "@/utils/responses";
import router from "@/router";
import { nextTick } from 'vue'
import { getValue as getValueAdapter, setValue as setValueAdapter } from './storageAdapter';
import stockRepository from './StockRepository';

export const ZRA_DETAILS = 'zra_details'
export const DEFAULT_STORE = 'default_store'
export const ALL_STORES = 'all_stores'
export const FRANCHISE_SETTINGS = 'franchise_settings'
export const REGISTRATION_DETAILS = 'registration_details'

export const ADMIN = 'admin'
export const CATEGORIES = 'categories'
export const AllUsers = 'all_users'
export const AllSecurities = 'securities'
export const CurrentUser = 'current_user'
export const STOCK = 'stock'
export const STOCK_IMAGES = 'stockImages'
export const STOCK_SYNC_STATE = 'stock_sync_state'
export const STOCK_DIRTY_PLU_STATE = 'stock_dirty_plu_state'
export const PAYMENT_METHODS = 'payment_methods'
export const STOCK_SETTING = 'stock_setting'
export const TAX_LABELS = 'taxLabels'

export const HAS543 = 'has543'
export const HAS_LOYALTY = 'hasLoyalty'
export const HASKAZANG = 'hasKazang'
export const KAZANGSESSION = 'kazangSession'
export const KAZANGPRODUCTS = 'kazangProductList'
export const STORE_CURRENCIES = 'storeCurrencies'

export const FLOAT = 'float'
export const DEVICE_SETTINGS = 'device_settings'
export const SAVED_TABLES = 'saved_tables'
export const ANDROID_SETTINGS = 'android_settings'  // Android-specific settings (not synced to server)

export const BASE_CURRENCY = 'base_currency'
export const ZED_MOBILE_STATUS = 'zedMobileStatus'

export const DISCOUNT_SPECIALS = 'discountSpecials'
export const MULTI_SPECIALS = 'multiSpecials'
export const COMBO_SPECIALS = 'comboSpecials'
export const SYSTEM_SETTINGS = 'systemSettings'

export const SUPERVISOR = 'supervisor'
export const STORE_EXPIRY = 'store_expiry'
export const FLOAT_PENDING = 'float_pending'

/**
 * Storage key for the deeplink session minted by
 * {@code POST /posAuth/{DB}/login} on Franchise V2 (port 7082).
 * Carries only an opaque {@code sessionId} + {@code sessionExpiresAt}
 * - never the user's plaintext password. Replaces the legacy
 * `?auth=` deeplink payload which was base64-encoded JSON containing
 * the password. See
 * `docs/exec-plans/active/2026-06-22-deeplink-session-rollout.md`.
 */
export const CURRENT_SESSION = 'current_session'

export const OFFLINE_CUSTOMERS = 'offline_customers'
export const CUSTOMER_ID_MAP = 'customer_id_map'

export const LODGING_API_KEY = 'lodging_api_key'
export const LODGING_TENANT_ID = 'lodging_tenant_id'
export const LODGING_NAME = 'lodging_name'
export const ENHANCED_STORES = 'enhanced_stores'

// Cooking Instruction Catalog storage keys
export const INSTRUCTION_CATALOG_INDEX = 'instruction_catalog_index'

const notEmpty = (val) => {
    if (!val) return false
    return val !== ''
}
const encryptAdmin = (val) => {
    return window.btoa(val);
}
const decryptAdmin = (val) => {
    if (!val) return undefined;
    if (val.toString().charAt(0) === `"`) {
        val = val.toString().slice(1, -1);
    }
    return JSON.parse(window.atob(val))
}

// Storage method helpers - using SQLite via storageAdapter
const getValue = async (key, storeName = 'main_store') => {
    return getValueAdapter(key, storeName);
};

const setValue = async (key, value, storeName = 'main_store') => {
    return setValueAdapter(key, value, storeName);
};

const normalizePluKey = (plu) => String(plu ?? '').toLowerCase();

const buildStockImageIndex = (images = []) => {
    const imageIndex = new Map();
    for (let i = 0; i < images.length; i++) {
        const item = images[i];
        const key = normalizePluKey(item?.PLU);
        const image = typeof item?.Image === 'string' ? item.Image.trim() : item?.Image;
        if (!key || typeof image !== 'string' || image.length === 0) continue;
        imageIndex.set(key, image);
    }
    return imageIndex;
};

const attachImagesToStock = (stock = [], imageIndex = new Map()) => {
    if (!stock.length || imageIndex.size === 0) return stock;
    return stock.map((item) => {
        const image = imageIndex.get(normalizePluKey(item?.PLU));
        if (!image) return item;
        return { ...item, Image: image };
    });
};

let stockImagesCache = null;
let stockImageIndexCache = null;

const setStockImagesCache = (images = []) => {
    stockImagesCache = Array.isArray(images) ? images : [];
    stockImageIndexCache = buildStockImageIndex(stockImagesCache);
};

const hydrateStockImagesCache = async () => {
    if (Array.isArray(stockImagesCache) && stockImageIndexCache) return;
    const storedImages = await getValue(STOCK_IMAGES);
    setStockImagesCache(Array.isArray(storedImages) ? storedImages : []);
};

const getCachedStockImages = async () => {
    await hydrateStockImagesCache();
    return stockImagesCache || [];
};

const getCachedStockImageIndex = async () => {
    await hydrateStockImagesCache();
    return stockImageIndexCache || new Map();
};

let stockSettingsCache = null;
let stockSettingsMapCache = null;
const createDefaultStockSetting = (plu = '') => ({
    plu: String(plu || ''),
    cookingLink: false,
    mtv: 0.00,
    printer: null,
    askForPrice: false,
    askForDescription: false,
    askForBoth: false,
    NonStock: false,
    tourism: false,
    getQtyFromPrice: false,
    password_protected: false,
    scale: false,
    lpoVatPreprocessed: false
});
const buildStockSettingsMap = (settings = []) => {
    const source = Array.isArray(settings) ? settings : [];
    return source.reduce((acc, setting) => {
        const key = normalizePluKey(setting?.plu ?? setting?.PLU);
        if (!key) return acc;
        acc[key] = setting;
        return acc;
    }, {});
};

export const Storage = {
    // Cooking Instruction Catalog helpers
    saveInstructionCatalog: async (storeDB, plu, catalog) => {
        try {
            if (!storeDB || !plu || !catalog) return false;
            const key = `instruction_catalog:${storeDB}:${plu}`;
            await setValue(key, catalog);
            // Maintain a simple index of cached PLUs per store for visibility/debug
            const idxKey = `${INSTRUCTION_CATALOG_INDEX}:${storeDB}`;
            const idx = (await getValue(idxKey)) || {};
            idx[plu] = { updatedAt: new Date().toISOString() };
            await setValue(idxKey, idx);
            return true;
        } catch (e) {
            console.warn('[Storage] Failed to save instruction catalog', storeDB, plu, e);
            return false;
        }
    },
    getInstructionCatalog: async (storeDB, plu) => {
        try {
            if (!storeDB || !plu) return null;
            const key = `instruction_catalog:${storeDB}:${plu}`;
            return await getValue(key);
        } catch (e) {
            console.warn('[Storage] Failed to get instruction catalog', storeDB, plu, e);
            return null;
        }
    },
    getInstructionCatalogIndex: async (storeDB) => {
        try {
            if (!storeDB) return {};
            const idxKey = `${INSTRUCTION_CATALOG_INDEX}:${storeDB}`;
            return (await getValue(idxKey)) || {};
        } catch (e) {
            console.warn('[Storage] Failed to get instruction catalog index', storeDB, e);
            return {};
        }
    },
    // Float pending helpers: track per-user pending float requirement when offline
    setFloatPending: async (username, pending = true) => {
        try {
            const map = (await getValue(FLOAT_PENDING)) || {};
            if (pending) {
                map[username] = true;
            } else if (map && Object.prototype.hasOwnProperty.call(map, username)) {
                delete map[username];
            }
            await setValue(FLOAT_PENDING, map);
        } catch (e) {
            console.warn('Failed to set float pending flag:', e);
        }
    },
    getFloatPending: async (username) => {
        try {
            const map = (await getValue(FLOAT_PENDING)) || {};
            return !!(map && map[username]);
        } catch (e) {
            return false;
        }
    },
    clearFloatPending: async (username) => {
        return Storage.setFloatPending(username, false);
    },
    /**
     * Store-only disk wipe. Clears the bound store and store-scoped session
     * data so Switch Store can open the picker without re-prompting franchise.
     * KEEP admin + all_stores (franchise Welcome footprint). Full franchise
     * wipe is signOutOfFranchise in the Vuex store, not this method.
     */
    logOutofStore: async () => {
        const emptyUser = {
            Type: "",
            Username: "",
            id: "",
            Surname: "",
            Name: "",
            Password: ""
        };

        // default_store is the gate that rehydrates "still in a store" and
        // can push poslogin from setup/login — must clear on store leave.
        try {
            await setValue(DEFAULT_STORE, null);
            store.commit('setDefaultStore', null);
        } catch (err) {
            console.warn('[Storage] clear default_store failed:', err?.message || err);
        }

        await Storage.setCurrentUser(emptyUser);
        await Storage.clearCurrentSession();

        await Storage.setAllUsers([]);
        try { await setValue('AllUsersMeta', null); } catch (_) { /* ignore */ }
        try { await Storage.setAllSecurities([]); } catch (_) { /* ignore */ }
        try { await setValue('AllSecuritiesMeta', null); } catch (_) { /* ignore */ }
        try { await Storage.setSupervisor(null); } catch (_) { /* ignore */ }

        // Catalogue is device-global — wipe so the next store cannot see
        // another store's stock or dirty flags.
        try {
            await Storage.clearLocalStock();
        } catch (err) {
            console.warn('[Storage] clearLocalStock on logout failed:', err?.message || err);
        }

        const storeScopedKeys = [
            CATEGORIES,
            PAYMENT_METHODS,
            BASE_CURRENCY,
            STORE_CURRENCIES,
            DISCOUNT_SPECIALS,
            MULTI_SPECIALS,
            COMBO_SPECIALS,
            TAX_LABELS,
            SYSTEM_SETTINGS,
            FRANCHISE_SETTINGS,
            REGISTRATION_DETAILS,
            STORE_EXPIRY,
            DEVICE_SETTINGS,
            FLOAT,
            FLOAT_PENDING,
            SAVED_TABLES,
            HAS543,
            HAS_LOYALTY,
            HASKAZANG,
            KAZANGSESSION,
            KAZANGPRODUCTS,
            ZED_MOBILE_STATUS,
            ZRA_DETAILS,
            OFFLINE_CUSTOMERS,
            CUSTOMER_ID_MAP,
            SUPERVISOR,
            STOCK_SETTING,
        ];
        await Promise.all(storeScopedKeys.map(async (key) => {
            try {
                await setValue(key, null);
            } catch (err) {
                console.warn(`[Storage] clear ${key} on logout failed:`, err?.message || err);
            }
        }));

        try {
            localStorage.removeItem('users');
            localStorage.removeItem('supportAgent');
            localStorage.removeItem('latestOfflineInvoiceSequence');
            localStorage.removeItem('currentSaleLines');
            localStorage.removeItem('apiKey');
        } catch (_) { /* ignore */ }

        // Do NOT clear admin or all_stores — franchise Welcome footprint.
    },

    /**
     * Remove persisted stock, dirty flags, and sync checkpoints from
     * device storage. Call on sign-out and when StoreDB changes.
     * After wipe, the next getAllStock must full-snapshot (not delta-merge
     * onto an empty/wrong base).
     */
    clearLocalStock: async () => {
        try {
            const cleared = await stockRepository.clearAllStock()
            if (!cleared) {
                await setValue(STOCK, [])
            }
        } catch (error) {
            console.error('[Storage] clearAllStock failed:', error)
            try {
                await setValue(STOCK, [])
            } catch (_) { /* ignore legacy fallback */ }
            throw error
        }
        try {
            await setValue(STOCK, [])
        } catch (err) {
            console.warn('[Storage] clear legacy stock key failed:', err?.message || err)
        }
        try {
            await Storage.clearStockDirtyPlus()
        } catch (err) {
            console.warn('[Storage] clear stock dirty failed:', err?.message || err)
        }
        try {
            await setValue(STOCK_SYNC_STATE, {})
        } catch (err) {
            console.warn('[Storage] clear stock_sync_state failed:', err?.message || err)
        }
        try {
            await setValue(STOCK_SETTING, null)
        } catch (err) {
            console.warn('[Storage] clear stock_setting failed:', err?.message || err)
        }
        try {
            await setValue(STOCK_IMAGES, [])
            store.commit('setStockImages', [])
        } catch (err) {
            console.warn('[Storage] clear stock images failed:', err?.message || err)
        }
    },
    getCurrentStoreDB: async () => {
        if (!store.getters.isHydrated) {
            await new Promise((resolve) => {
                const interval = setInterval(() => {
                    if (store.getters.isHydrated) {
                        clearInterval(interval);
                        resolve();
                    }
                }, 50);  // Check every 50ms
            });
        }
        let defaultStore = store.getters.getDefaultStore;
        if (defaultStore) {
            if (!defaultStore) {
                axios.get(reportTomcatApi + `getFranchiseByDB?DB=${defaultStore.StoreDB}`)
                    .then(({ data }) => store.commit(`setAdmin`, data))
            }
            return defaultStore.StoreDB;
        }
        if (!window.androidMode) {
            let storeDB;
            try {
                let response = await axios.get(hardwareApi + `utils/getStoreDB`);
                if (response.status === 404) {
                    responses.showWarning(`Request for hardware service update`)
                    return null;
                }
                storeDB = response.data;
                if (storeDB && storeDB !== 'none') {
                    console.trace(`Called`)
                    return axios.get(reportTomcatApi + `getStoresByDB?DB=${storeDB}`)
                        .then(async ({ data }) => {
                            store.commit(`setRegisteredStores`, data);
                            let currentStore = data.find(s => s.StoreDB === storeDB);
                            if (currentStore) {
                                await Storage.setDefaultStore(currentStore);
                                axios.get(reportTomcatApi + `getFranchiseByDB?DB=${storeDB}`)
                                    .then(({ data }) => store.commit(`setAdmin`, data))
                                if (router.currentRoute.name === `login` || router.currentRoute.name === `poslogin` || router.currentRoute.name === `setup`) {
                                    router.push({ name: `poslogin` })
                                }
                                return currentStore.StoreDB;
                            }
                        })
                }
            } catch (e) {
            }
        }
        return null;
    },
    setAdminUser: async (user) => {
        const encrypted = encryptAdmin(JSON.stringify(user));
        await setValue(ADMIN, encrypted, 'admin_users');
        store.commit('setAdmin', user);
    },
    getAdminUser: async () => {
        await Storage.waitForHydrate();
        const hasCreds = (admin) => {
            if (!admin || typeof admin !== 'object') return false;
            const email = admin.email || admin.Email || admin.username || admin.Username;
            const password = admin.password || admin.Password;
            return !!(
                email && String(email).trim().length > 0 &&
                password && String(password).trim().length > 0
            );
        };

        let value = store.getters.getAdmin;
        // Empty `{}` after franchise wipe is truthy — do not treat as session.
        if (hasCreds(value)) return value;

        try {
            value = await getValue(ADMIN, 'admin_users');
            if (value) {
                const decoded = decryptAdmin(value);
                if (hasCreds(decoded)) return decoded;
            }
        } catch (error) {
            console.error('Storage access error:', error);
        }
        return null;
    },

    setAllStores: async (payload) => {
        // store/index.js state: registeredStores
        await setValue(ALL_STORES, payload);
        store.commit('setRegisteredStores', payload);
    },

    getAllStores: async () => {
        // Empty [] is truthy in JS — after store-only sign-out Vuex may hold
        // [] while SQLite still has the franchise store list. Treat empty as miss.
        let value = store.getters.getRegisteredStores;
        if (Array.isArray(value) && value.length > 0) return value;
        value = await getValue(ALL_STORES);
        return value;
    },

    setFranchiseSettings: async (payload) => {
        await setValue(FRANCHISE_SETTINGS, payload);
        store.commit('setFranchiseSettings', payload);
    },

    setRegistrationDetails: async (payload) => {
        await setValue(REGISTRATION_DETAILS, payload);
        store.commit('setRegistrationDetails', payload);
    },

    getFranchiseSettings: async () => {
        let value = store.getters.getFranchiseSettings;
        if (value) return value;
        value = await getValue(FRANCHISE_SETTINGS);
        return value;
    },

    getRegistrationDetails: async () => {
        let value = store.getters.getRegistrationDetails;
        if (value) return value;
        value = await getValue(REGISTRATION_DETAILS);
        return value;
    },

    setStoreExpiry: async (expiryDate) => {
        // Store expiry date as ISO string for offline login validation
        await setValue(STORE_EXPIRY, expiryDate);
    },

    getStoreExpiry: async () => {
        // Retrieve cached expiry date for offline login validation
        const value = await getValue(STORE_EXPIRY);
        return value;
    },

    setZRADetails: async (payload) => {
        await setValue(ZRA_DETAILS, payload);
    },

    setCategories: async (categories) => {
        await setValue(CATEGORIES, categories);
        store.commit('setCategories', categories);
    },

    getCategories: async () => {
        let value = store.getters.getCategories;
        if (value) return value;
        value = await getValue(CATEGORIES);
        return value;
    },

    setHas543: async (has543) => {
        await setValue(HAS543, has543);
        store.commit('setHas543', has543);
    },
    getHas543: async () => {
        return getValue(HAS543);
    },

    setStockImages: async (payload) => {
        if (!Array.isArray(payload) || payload.length === 0) {
            const images = [];
            setStockImagesCache(images);
            await setValue(STOCK_IMAGES, images);
            store.commit('setStockImages', images);
            return;
        }

        const deduped = new Map();
        for (let i = 0; i < payload.length; i++) {
            const itemImage = payload[i];
            const key = normalizePluKey(itemImage?.PLU);
            const image = typeof itemImage?.Image === 'string' ? itemImage.Image.trim() : itemImage?.Image;
            if (!key || typeof image !== 'string' || image.length === 0) continue;
            deduped.set(key, { PLU: itemImage.PLU, Image: image });
        }

        const images = [...deduped.values()];
        setStockImagesCache(images);
        await setValue(STOCK_IMAGES, images);
        store.commit('setStockImages', images);
    },
    setStockSyncState: async (storeDb, payload = {}) => {
        if (!storeDb) return;
        const allStates = await getValue(STOCK_SYNC_STATE) || {};
        const current = allStates[storeDb] || {};
        allStates[storeDb] = {
            ...current,
            ...payload
        };
        await setValue(STOCK_SYNC_STATE, allStates);
    },
    getStockSyncState: async (storeDb) => {
        const allStates = await getValue(STOCK_SYNC_STATE) || {};
        if (!storeDb) return allStates;
        return allStates[storeDb] || null;
    },
    getStockDirtyPlus: async (storeDb) => {
        const allStates = await getValue(STOCK_DIRTY_PLU_STATE) || {};
        if (!storeDb) return allStates;
        return allStates[storeDb] || {};
    },
    markStockDirtyPlus: async (storeDb, dirtyEntries = []) => {
        if (!storeDb || !Array.isArray(dirtyEntries) || dirtyEntries.length === 0) return;
        const allStates = await getValue(STOCK_DIRTY_PLU_STATE) || {};
        const current = allStates[storeDb] || {};
        const nowIso = new Date().toISOString();

        for (let i = 0; i < dirtyEntries.length; i++) {
            const entry = dirtyEntries[i] || {};
            const pluKey = normalizePluKey(entry.plu || entry.PLU);
            if (!pluKey) continue;
            const qtyDelta = Number(entry.qtyDelta ?? entry.delta ?? 0);
            const prev = current[pluKey] || {};
            current[pluKey] = {
                qtyDelta: Number(prev.qtyDelta || 0) + (Number.isFinite(qtyDelta) ? qtyDelta : 0),
                updatedAt: entry.updatedAt || nowIso,
                reason: entry.reason || prev.reason || 'sale'
            };
        }

        allStates[storeDb] = current;
        await setValue(STOCK_DIRTY_PLU_STATE, allStates);
    },
    clearStockDirtyPlus: async (storeDb, plus = []) => {
        const allStates = await getValue(STOCK_DIRTY_PLU_STATE) || {};
        if (!storeDb) {
            await setValue(STOCK_DIRTY_PLU_STATE, {});
            return;
        }

        if (!allStates[storeDb]) return;

        if (!Array.isArray(plus) || plus.length === 0) {
            delete allStates[storeDb];
            await setValue(STOCK_DIRTY_PLU_STATE, allStates);
            return;
        }

        const current = allStates[storeDb];
        for (let i = 0; i < plus.length; i++) {
            const pluKey = normalizePluKey(plus[i]);
            if (!pluKey) continue;
            delete current[pluKey];
        }
        allStates[storeDb] = current;
        await setValue(STOCK_DIRTY_PLU_STATE, allStates);
    },
    applySaleStockDeductions: async (storeDb, saleItems = [], options = {}) => {
        const markDirty = options.markDirty !== false;
        if (!Array.isArray(saleItems) || saleItems.length === 0) {
            return { ok: true, updated: 0, dirty: 0, failed: 0, missing: 0 };
        }

        const qtyByPlu = new Map();
        for (let i = 0; i < saleItems.length; i++) {
            const line = saleItems[i] || {};
            const pluKey = normalizePluKey(line.Plu || line.PLU || line.plu);
            if (!pluKey) continue;
            const qty = Number(line.Qty ?? line.qty ?? line.quantity ?? 0);
            if (!Number.isFinite(qty) || qty <= 0) continue;
            qtyByPlu.set(pluKey, Number(qtyByPlu.get(pluKey) || 0) + qty);
        }

        if (qtyByPlu.size === 0) {
            return { ok: true, updated: 0, dirty: 0, failed: 0, missing: 0 };
        }

        const stock = await Storage.getRawStock();
        if (!Array.isArray(stock) || stock.length === 0) {
            return {
                ok: false,
                updated: 0,
                dirty: 0,
                failed: qtyByPlu.size,
                missing: qtyByPlu.size,
                reason: 'stock_not_cached'
            };
        }

        const nextStock = [...stock];
        const stockByPlu = new Map();
        for (let i = 0; i < stock.length; i++) {
            const item = stock[i];
            const pluKey = normalizePluKey(item?.PLU);
            if (!pluKey) continue;
            stockByPlu.set(pluKey, { item, index: i });
        }

        const nowIso = new Date().toISOString();
        const dirtyEntries = [];
        const fallbackUpdates = [];
        let updatedCount = 0;
        let failedCount = 0;
        let missingCount = 0;

        for (const [pluKey, soldQty] of qtyByPlu.entries()) {
            const stockMatch = stockByPlu.get(pluKey);
            if (!stockMatch?.item) {
                missingCount++;
                continue;
            }

            const existing = stockMatch.item;
            const currentQty = Number(existing?.QTY ?? existing?.Qty ?? existing?.qty ?? 0);
            if (!Number.isFinite(currentQty)) {
                missingCount++;
                continue;
            }

            const nextQty = Number((currentQty - soldQty).toFixed(3));
            const updatedItem = {
                ...existing,
                QTY: nextQty,
                UpdatedAt: nowIso,
                updatedAt: nowIso
            };
            if (existing?.Qty !== undefined) updatedItem.Qty = nextQty;
            if (existing?.qty !== undefined) updatedItem.qty = nextQty;

            const dirtyEntry = {
                plu: existing.PLU || pluKey,
                qtyDelta: soldQty,
                updatedAt: nowIso,
                reason: options.reason || 'sale'
            };

            const saved = await stockRepository.upsertStockItem(updatedItem);
            if (saved) {
                updatedCount++;
                dirtyEntries.push(dirtyEntry);
                nextStock[stockMatch.index] = updatedItem;
            } else {
                fallbackUpdates.push({ index: stockMatch.index, updatedItem, dirtyEntry });
            }
        }

        if (fallbackUpdates.length > 0) {
            for (let i = 0; i < fallbackUpdates.length; i++) {
                const fallback = fallbackUpdates[i];
                nextStock[fallback.index] = fallback.updatedItem;
            }
            try {
                await setValue(STOCK, nextStock);
                updatedCount += fallbackUpdates.length;
                for (let i = 0; i < fallbackUpdates.length; i++) {
                    dirtyEntries.push(fallbackUpdates[i].dirtyEntry);
                }
            } catch (fallbackErr) {
                failedCount += fallbackUpdates.length;
                console.warn('[Storage] Failed to persist fallback stock deduction update', fallbackErr);
            }
        }

        if (markDirty && dirtyEntries.length > 0 && storeDb) {
            await Storage.markStockDirtyPlus(storeDb, dirtyEntries);
        }

        return {
            ok: failedCount === 0,
            updated: updatedCount,
            dirty: dirtyEntries.length,
            failed: failedCount,
            missing: missingCount
        };
    },


    setHasKazang: async (payload) => {
        await setValue(HASKAZANG, payload);
        store.commit('setHasKazang', payload);
    },

    getHasKazang: async () => {
        const value = await getValue(HASKAZANG);
        return JSON.parse(value);
    },

    getZRADetails: async () => {
        const value = await getValue(ZRA_DETAILS);
        return JSON.parse(value);
    },
    setDefaultStore: async (defaultStore) => {
        await setValue(DEFAULT_STORE, defaultStore);
        store.commit('setDefaultStore', defaultStore);
    },
    getDefaultStore: async () => {
        if (!store.getters.isHydrated) {
            await new Promise((resolve) => {
                const interval = setInterval(() => {
                    if (store.getters.isHydrated) {
                        clearInterval(interval);
                        resolve();
                    }
                }, 50);  // Check every 50ms
            });
        }
        let value = store.getters.getDefaultStore;
        if (value) {
            if (!value) {
                axios.get(reportTomcatApi + `getFranchiseByDB?DB=${value.StoreDB}`)
                    .then(({ data }) => store.commit(`setAdmin`, data))
            }
            setTimeout(() => {
                let name = router.currentRoute.name;
                if (name === 'login' || name === 'poslogin' || name === 'setup') {
                    router.push({ name: 'poslogin' }).catch((err) => {
                        if (err.name !== 'NavigationDuplicated') {
                            console.error(err)
                        }
                    })
                }
            }, 0)
            return value;
        }
        value = await getValue(DEFAULT_STORE);
        if (value) {
            if (value) {
                axios.get(reportTomcatApi + `getFranchiseByDB?DB=${value.StoreDB}`)
                    .then(({ data }) => store.commit(`setAdmin`, data))
            }
            setTimeout(() => {
                let name = router.currentRoute.name;
                if (name === 'login' || name === 'setup') {
                    router.push({ name: 'poslogin' }).catch((err) => {
                        if (err.name !== 'NavigationDuplicated') {
                            console.error(err)
                        }
                    })
                }
            }, 0)
            return value;
        }
        if (!window.androidMode) {
            let storeDB;
            try {
                let response = await axios.get(hardwareApi + `utils/getStoreDB`);
                if (response.status === 404) {
                    responses.showWarning(`Request for hardware service update`)
                    return null;
                }
                storeDB = response.data;
                if (storeDB && storeDB !== 'none') {
                    console.trace(`Called`)
                    return axios.get(reportTomcatApi + `getStoresByDB?DB=${storeDB}`)
                        .then(async ({ data }) => {
                            store.commit(`setRegisteredStores`, data);
                            let currentStore = data.find(s => s.StoreDB === storeDB);
                            if (currentStore) {
                                await Storage.setDefaultStore(currentStore);;
                                axios.get(reportTomcatApi + `getFranchiseByDB?DB=${storeDB}`)
                                    .then(({ data }) => store.commit(`setAdmin`, data))
                                setTimeout(() => {
                                    let name = router.currentRoute.name;
                                    if (name === 'login' || name === 'setup') {
                                        router.push({ name: 'poslogin' }).catch((err) => {
                                            if (err.name !== 'NavigationDuplicated') {
                                                console.error(err)
                                            }
                                        })
                                    }
                                }, 0)

                                return currentStore;
                            }
                        })
                }
            } catch (e) {
            }
        }
        return null;
    },

    getCurrentStoreID: async () => {
        let value = store.getters.getFranchiseSettings;
        if (value) return value.StoreId;
        value = await getValue(FRANCHISE_SETTINGS);
        if (!value) return null;
        return value.StoreId;
    },

    setAllUsers: async (users) => {
        // store/module/userModule.js state: users
        await setValue(AllUsers, users);
        // Store metadata with timestamp for offline login validation
        await setValue('AllUsersMeta', {
            lastSyncedAt: new Date().toISOString()
        });
        store.commit('setAllUsers', users);
    },
    getAllUsers: async () => {
        let value = store.getters.getUsers;
        if (value) return value;
        value = await getValue(AllUsers);
        return value;
    },
    getAllUsersMeta: async () => {
        return await getValue('AllUsersMeta');
    },
    setAllSecurities: async (users) => {
        await setValue(AllSecurities, users);
        // Store metadata with timestamp for offline permission checks
        await setValue('AllSecuritiesMeta', {
            lastSyncedAt: new Date().toISOString()
        });
        store.commit('setAllSecurities', users);
    },
    getAllSecurities: async () => {
        let value = store.getters.getSecurities;
        if (value) return value;
        value = await getValue(AllSecurities);
        return value;
    },
    getAllSecuritiesMeta: async () => {
        return await getValue('AllSecuritiesMeta');
    },

    setCurrentUser: async (currentUser) => {
        await setValue(CurrentUser, currentUser);
        store.commit('setCurrentUser', currentUser);
    },
    getCurrentUser: async () => {
        let value = store.getters.getCurrentUser;
        if (value) return value;
        value = await getValue(CurrentUser);
        return value;
    },

    /**
     * Persist the deeplink session payload returned by
     * {@code POST /posAuth/{DB}/login}. The session is scoped to the
     * given storeDB so a second-store login on the same device does
     * not stomp on the first store's session.
     *
     * <p>Payload shape (subset used by the backoffice-module deeplink
     * opener):
     * <pre>
     * {
     *   sessionId:        string  // 64-char hex from SecureRandom
     *   sessionExpiresAt: string  // ISO-8601 datetime
     *   username:         string  // emp.Username
     *   empId:            string  // emp.id
     * }
     * </pre>
     *
     * @param {Object} payload session payload from the login response
     * @param {string} storeDb the StoreDB this session was issued for
     */
    setCurrentSession: async (payload, storeDb) => {
        const value = Object.assign({}, payload || {}, { storeDb: storeDb || null });
        await setValue(CURRENT_SESSION, value);
    },

    /**
     * Read the persisted deeplink session.
     *
     * <p>The previous implementation returned {@code null} when the
     * persisted session's {@code storeDb} did not match
     * {@code expectedStoreDb}. That caused false negatives whenever
     * the cashier switched stores (or the default store got out of
     * sync with the persisted session) and {@code openOnlineBackOffice}
     * then fell back to opening a bare {@code LytningCore://open}
     * URL — which launched Lytning Core without auto-login instead of
     * opening the requested page.
     *
     * <p>Store-scoped revocation is enforced by the backend:
     * {@code GET /posAuth/{DB}/session/{id}} only finds rows in
     * {@code <DB>.loginaudit} matching the {@code DB} URL parameter. A
     * session minted for store A cannot be redeemed against store B
     * even if the URL is forged. The frontend cross-check was
     * redundant and was causing the deep-link regression.
     *
     * <p>Mirrors the Retail POS behaviour applied 2026-06-24 (gate
     * removed there as part of the same session-deeplink rollout).
     *
     * @param {string} [expectedStoreDb] informational only - logged, not enforced
     * @returns {Promise<?Object>} the session payload or null when absent
     */
    getCurrentSession: async (expectedStoreDb) => {
        const value = await getValue(CURRENT_SESSION);
        if (!value) {
            // eslint-disable-next-line no-console
            console.log(`[deeplink][Storage] getCurrentSession expectedStoreDb=${expectedStoreDb || 'null'} persisted=null match=n/a`);
            return null;
        }
        // eslint-disable-next-line no-console
        console.log(`[deeplink][Storage] getCurrentSession expectedStoreDb=${expectedStoreDb || 'null'} persisted=${JSON.stringify(value)} persistedStoreDb=${value.storeDb || 'undefined'} match=n/a (gate removed)`);
        return value;
    },

    /**
     * Clear the persisted deeplink session. Called on logout / store
     * switch to prevent a stale token being used to open BackOffice.
     */
    clearCurrentSession: async () => {
        await setValue(CURRENT_SESSION, null);
    },

    stockByPLU: async (stock) => {
        const persistedToSql = await stockRepository.replaceStock(stock);
        if (!persistedToSql) {
            await setValue(STOCK, stock);
        }
    },
    getRawStock: async () => {
        const sqlStock = await stockRepository.getAllStock();
        if (Array.isArray(sqlStock) && sqlStock.length > 0) {
            return sqlStock;
        }

        const legacyStock = await getValue(STOCK) ?? [];
        if (legacyStock.length > 0) {
            // One-time lazy migration path for devices that already had stock in legacy KV storage.
            await stockRepository.replaceStock(legacyStock);
        }
        return legacyStock;
    },
    getAllStock: async () => {
        let stock = await Storage.getRawStock();
        if (stock.length === 0) return [];
        const imageIndex = await getCachedStockImageIndex();
        if (imageIndex.size === 0) return stock;
        return attachImagesToStock(stock, imageIndex);
    },
    getAllStockImages: async () => {
        return await getCachedStockImages();
    },
    getByPlu: async (plu) => {
        const sqlItem = await stockRepository.getByPlu(plu);
        let item = sqlItem;
        if (!item) {
            const stock = await getValue(STOCK) ?? [];
            const pluKey = normalizePluKey(plu);
            item = stock.find(stockItem => normalizePluKey(stockItem?.PLU) === pluKey);
        }
        if (!item) return item;

        const imageIndex = await getCachedStockImageIndex();
        if (imageIndex.size === 0) return item;
        const image = imageIndex.get(normalizePluKey(item?.PLU));
        if (!image) return item;
        return { ...item, Image: image };
    },
    getFastLine: async () => {
        let stock = await stockRepository.getFastLine();
        if (!Array.isArray(stock)) {
            stock = await getValue(STOCK) ?? [];
            stock = stock.filter(sc => sc.FastLine === 1);
        }

        const imageIndex = await getCachedStockImageIndex();
        if (imageIndex.size > 0) {
            stock = attachImagesToStock(stock, imageIndex);
        }
        return stock;
    },
    searchStockDesc: async (query, limit = 5) => {
        let result = await stockRepository.searchStockDesc(query, limit);
        if (!Array.isArray(result)) {
            let stock = await getValue(STOCK) ?? [];
            result = stock.filter(s => s.Description.toLowerCase().includes(query.description.toLowerCase())
                || s.Description2?.toLowerCase().includes(query.description.toLowerCase())
                || s.PLU.toLowerCase().includes(query.description.toLowerCase())
                || s.barcode.toLowerCase().includes(query.description.toLowerCase())
            );

            if (notEmpty(query.category)) {
                result = result.filter(s => s.Department.toLowerCase() === query.category.toLowerCase());
            }
            if (notEmpty(query.subCategory)) {
                result = result.filter(s => s.SubDep.toLowerCase() === query.subCategory.toLowerCase());
            }
            if (notEmpty(query.sub2Category)) {
                result = result.filter(s => s.Subdep2.toLowerCase() === query.sub2Category.toLowerCase());
            }

            // Sort results by description
            result.sort((a, b) => a.Description.localeCompare(b.Description));

            // Apply limit for predictions
            result = result.slice(0, limit);
        }

        if (result.length === 0) return result;

        const imageIndex = await getCachedStockImageIndex();
        if (imageIndex.size === 0) return result;
        return attachImagesToStock(result, imageIndex);
    },
    searchStock: async (query, limit = 50, offset = 0) => {
        let result = [];
        const sqlResult = await stockRepository.searchStock(query, limit, offset);
        if (sqlResult && Array.isArray(sqlResult.rows)) {
            result = sqlResult.rows;
            store.commit('setStockLength', Number(sqlResult.total || 0));
        } else {
            let stock = await getValue(STOCK) ?? [];
            if (query.category !== '' && query.subCategory !== '' && query.sub2Category !== '') {
                result = stock.filter(s => s.Department.toLowerCase() === query.category.toLowerCase())
                    .filter(s2 => s2.SubDep.toLowerCase() === query.subCategory.toLowerCase())
                    .filter(s3 => s3.Subdep2.toLowerCase() === query.sub2Category.toLowerCase());
            } else if (query.category !== '' && query.subCategory !== '' && query.sub2Category === '') {
                result = stock.filter(s => s.Department.toLowerCase() === query.category.toLowerCase())
                    .filter(s2 => s2.SubDep.toLowerCase() === query.subCategory.toLowerCase());
            } else if (query.category !== '' && query.sub2Category === '' && query.subCategory === '') {
                result = stock.filter(s => s.Department.toLowerCase() === query.category.toLowerCase());
            }

            if (query.description) {
                result = result.filter(s => s.Description.toLowerCase().includes(query.description.toLowerCase())
                    || s.PLU.toLowerCase().includes(query.description.toLowerCase())
                    || s.barcode.toLowerCase().includes(query.description.toLowerCase())
                );
            }

            store.commit('setStockLength', result.length)
            result = result.slice(offset, offset + limit);
        }

        if (result.length === 0) return [];

        // Fetch images
        const imageIndex = await getCachedStockImageIndex();
        if (imageIndex.size > 0) {
            result = attachImagesToStock(result, imageIndex);
        }

        // Sort results
        return result.sort((a, b) => {
            return a.Description.localeCompare(b.Description);
        });
    },
    editStockItem: async payload => {
        const saved = await stockRepository.upsertStockItem(payload);
        if (saved) return;

        let stock = await getValue(STOCK) ?? [];
        stock = stock.map(s => {
            if (s.PLU === payload.PLU) {
                return payload
            }
            return s;
        })
        await Storage.stockByPLU(stock);
    },
    setFloat: async (payload) => {
        await setValue(FLOAT, payload)
        store.commit('setUserFloat', payload);
    },
    getFloat: async () => {
        let value = store.getters.getFloats;
        if (value) return value;
        value = await getValue(FLOAT);
        return value;
    },
    setDeviceSettings: async (payload) => {
        await setValue(DEVICE_SETTINGS, payload)
        store.commit('setDeviceSettings', payload)
    },

    // Alias used across the app; ensure it exists and persists atomically with deep-merge semantics
    saveDeviceSettings: async (payload) => {
        try {
            if (!payload || typeof payload !== 'object') {
                console.warn('[Storage.saveDeviceSettings] Ignored non-object payload');
                return;
            }
            // Load existing settings from persistent store
            let existing = await getValue(DEVICE_SETTINGS);
            try {
                if (typeof existing === 'string') existing = JSON.parse(existing);
            } catch (_) { /* keep raw */ }
            if (!existing || typeof existing !== 'object') existing = {};

            // Merge without overwriting with undefined values
            const merged = { ...existing };
            Object.keys(payload).forEach((key) => {
                const val = payload[key];
                if (val !== undefined) merged[key] = val;
            });

            // Integrity rule: if this PC is main, clear peer IP
            if (merged.isMainTableServer === true) {
                merged.mainTableServerIP = null;
            }

            await setValue(DEVICE_SETTINGS, merged);
            store.commit('setDeviceSettings', merged);
        } catch (e) {
            console.error('[Storage.saveDeviceSettings] Failed to persist device settings:', e);
            // Fallback to original behavior to avoid total failure
            try {
                await setValue(DEVICE_SETTINGS, payload);
                store.commit('setDeviceSettings', payload);
            } catch (_) { }
        }
    },

    getDeviceSettings: async () => {
        let value = store.getters.getDeviceSettings;
        if (value) return value;
        value = await getValue(DEVICE_SETTINGS);
        if (!value) return {};
        return value;
    },

    // Unified Table Server Configuration
    // Provides a single source of truth for table server settings
    getTableServerConfig: async () => {
        const settings = await Storage.getDeviceSettings();

        // Generate or retrieve PC identifier
        let pcIdentifier = settings.pcIdentifier;
        if (!pcIdentifier) {
            // Generate unique identifier based on PC name + timestamp
            try {
                const pcName = await window.hardwareIPC?.getPcName?.() || 'unknown';
                pcIdentifier = `${pcName}-${Date.now()}`;
                // Save it for future use
                await Storage.saveDeviceSettings({ pcIdentifier });
            } catch (e) {
                pcIdentifier = `pc-${Date.now()}`;
            }
        }

        return {
            isMainTableServer: settings.isMainTableServer || false,
            mainTableServerIP: settings.mainTableServerIP || null,
            pcIdentifier: pcIdentifier,
            lastDiscoveryAt: settings.lastDiscoveryAt || null,
            discoveryMethod: settings.discoveryMethod || 'none' // 'bonjour', 'manual', 'broadcast', 'none'
        };
    },

    setTableServerConfig: async (config) => {
        try {
            const updates = {};

            if (config.isMainTableServer !== undefined) {
                updates.isMainTableServer = config.isMainTableServer;
                // If setting as main, clear the peer IP
                if (config.isMainTableServer === true) {
                    updates.mainTableServerIP = null;
                }
            }

            if (config.mainTableServerIP !== undefined) {
                updates.mainTableServerIP = config.mainTableServerIP;
                // If setting peer IP, ensure not marked as main
                if (config.mainTableServerIP) {
                    updates.isMainTableServer = false;
                }
            }

            if (config.pcIdentifier !== undefined) {
                updates.pcIdentifier = config.pcIdentifier;
            }

            if (config.lastDiscoveryAt !== undefined) {
                updates.lastDiscoveryAt = config.lastDiscoveryAt;
            }

            if (config.discoveryMethod !== undefined) {
                updates.discoveryMethod = config.discoveryMethod;
            }

            await Storage.saveDeviceSettings(updates);
            return true;
        } catch (e) {
            console.error('[Storage.setTableServerConfig] Failed:', e);
            return false;
        }
    },

    getAllTables: async () => {
        let value = store.getters.getAllTables;
        if (value) return value;
        value = await getValue(SAVED_TABLES);
        return value;
    },

    setTables: async (payload) => {
        await setValue(SAVED_TABLES, payload)
    },

    createTable: async (payload) => {
        let value = await getValue(SAVED_TABLES);
        if (!value) {
            value = {
                value: JSON.stringify([]),
            }
        }
        let tables = JSON.parse(value);
        if (!tables) {
            tables = []
        }
        tables.push(payload)
        await setValue(SAVED_TABLES, JSON.stringify(tables));
    },

    editTable: async (payload) => {
        const value = await getValue(SAVED_TABLES);
        let tables = JSON.parse(value);
        tables = tables.map(table => {
            if (table.SaleNum === payload.SaleNum) {
                return payload
            }
            return table;
        })
        await setValue(SAVED_TABLES, JSON.stringify(tables));
    },

    deleteTable: async (salenum) => {
        const value = await getValue(SAVED_TABLES);
        let tables = JSON.parse(value);
        tables = tables.filter(table => table.SaleNum !== salenum);
        await setValue(SAVED_TABLES, JSON.stringify(tables));
    },

    clear: async () => {
        await clear();
    },
    clearAll: async () => {
        try {
            await Storage.clearAll();
        } catch (error) {
            console.error('Failed to clear storage:', error);
        }
    },


    setStockSettings: async (stock) => {
        const safeStock = Array.isArray(stock) ? stock : [];
        await setValue(STOCK_SETTING, safeStock);
        stockSettingsCache = safeStock;
        stockSettingsMapCache = buildStockSettingsMap(safeStock);
        store.commit('setStockSetting', safeStock);
    },
    getStockSetting: async (plu) => {
        const pluKey = normalizePluKey(plu);
        if (!pluKey) return createDefaultStockSetting('');

        const byPluGetter = store.getters.getStockSettingByPlu;
        if (typeof byPluGetter === 'function') {
            const cachedByGetter = byPluGetter(pluKey);
            if (cachedByGetter) return cachedByGetter;
        }

        if (stockSettingsMapCache && stockSettingsMapCache[pluKey]) {
            return stockSettingsMapCache[pluKey];
        }

        let settings = store.getters.getStockSetting;
        if (!Array.isArray(settings) || settings.length === 0) {
            settings = Array.isArray(stockSettingsCache) && stockSettingsCache.length > 0
                ? stockSettingsCache
                : await getValue(STOCK_SETTING);
        }

        if (!Array.isArray(settings) || settings.length === 0) {
            return createDefaultStockSetting(plu);
        }

        stockSettingsCache = settings;
        stockSettingsMapCache = buildStockSettingsMap(settings);

        if (Array.isArray(store.getters.getStockSetting) && store.getters.getStockSetting.length === 0) {
            store.commit('setStockSetting', settings);
        }

        return stockSettingsMapCache[pluKey] || createDefaultStockSetting(plu);
    },
    getAllStockSettings: async () => {
        let settings = store.getters.getStockSetting;
        if (Array.isArray(settings) && settings.length > 0) return settings;
        if (Array.isArray(stockSettingsCache) && stockSettingsCache.length > 0) return stockSettingsCache;
        settings = await getValue(STOCK_SETTING);
        stockSettingsCache = Array.isArray(settings) ? settings : [];
        stockSettingsMapCache = buildStockSettingsMap(stockSettingsCache);
        return settings || [];
    },

    setPaymentMethods: async (data) => {
        await setValue(PAYMENT_METHODS, data);
        store.commit('setPaymentMethods', data);
    },
    getPaymentMethods: async () => {
        let value = store.getters.getPaymentMethods;
        if (value) return value;
        value = await getValue(PAYMENT_METHODS);
        return value;
    },

    setKazangSession: async (data) => {
        await setValue(KAZANGSESSION, data);
        store.commit('setKazangSession', data);
    },
    getKazangSession: async () => {
        let value = store.getters.getKazangSessionID;
        if (value) return value;
        value = await getValue(KAZANGSESSION);
        return value;
    },

    setKazangProductList: async (data) => {
        await setValue(KAZANGPRODUCTS, data);
        store.commit('setKazangProductList', data);
    },
    getKazangProductList: async () => {
        let value = store.getters.getKazangProductList;
        if (value) return value;
        value = await getValue(KAZANGPRODUCTS);
        return value;
    },

    setBaseCurrency: async (data) => {
        await setValue(BASE_CURRENCY, data)
        store.commit('setBaseCurrency', data);
    },
    getBaseCurrency: async () => {
        let value = store.getters.getBaseCurrency;
        if (value) return value;
        value = await getValue(BASE_CURRENCY);
        return value;
    },

    setDiscountSpecials: async (payload) => {
        setValue(DISCOUNT_SPECIALS, payload)
        store.commit('setDiscountSpecials', payload);
    },
    getDiscountSpecials: async () => {
        let value = store.getters.getDiscountSpecials;
        if (value) return value;
        value = await getValue(DISCOUNT_SPECIALS);
        return value;
    },

    setMultiSpecials: async (payload) => {
        setValue(MULTI_SPECIALS, payload)
        store.commit('setMultiSpecials', payload);
    },
    getMultiSpecials: async () => {
        let value = store.getters.getMultiSpecials;
        if (value) return value;
        value = await getValue(MULTI_SPECIALS);
        return value;
    },

    setComboSpecials: async (payload) => {
        setValue(COMBO_SPECIALS, payload)
        store.commit('setComboSpecials', payload);
    },
    getComboSpecials: async () => {
        let value = store.getters.getComboSpecials;
        if (value) return value;
        value = await getValue(COMBO_SPECIALS);
        return value;
    },

    setTaxLabels: async (payload) => {
        setValue(TAX_LABELS, payload)
        store.commit('setTaxLabels', payload);
    },
    getAllTaxLabels: async () => {
        let value = store.getters.getTaxLabels;
        if (value) return value;
        value = await getValue(TAX_LABELS);
        return value;
    },

    setSystemSettings: async (payload) => {
        setValue(SYSTEM_SETTINGS, payload)
        store.commit('setSystemSettings', payload);
    },
    getSystemSettings: async () => {
        let value = store.getters.getSystemSettings;
        if (value) return value;
        value = await getValue(SYSTEM_SETTINGS);
        return value;
    },

    // Android-specific settings (NOT synced to server)
    // These settings are local to the Android device only
    getAndroidSettings: async () => {
        try {
            const value = await getValue(ANDROID_SETTINGS);
            const base = value && typeof value === 'object' ? value : {};
            const androidHostEnabled = base.androidHostEnabled === true;
            const androidHostPort = Number(base.androidHostPort || 3500);
            const fallbackHost = String(
                base.mainPCName ||
                base.mainTableServerIP ||
                ''
            ).trim();

            if (fallbackHost) {
                return {
                    ...base,
                    androidTableServerEnabled: base.androidTableServerEnabled === true,
                    androidHostEnabled,
                    androidHostPort: Number.isFinite(androidHostPort) && androidHostPort > 0 ? androidHostPort : 3500,
                    mainPCName: fallbackHost
                };
            }

            // Backward compatibility: infer from shared device settings if Android-local host isn't present.
            const deviceSettings = await Storage.getDeviceSettings();
            const legacyHost = String(
                deviceSettings?.mainTableServerIP ||
                deviceSettings?.server ||
                ''
            ).trim();

            return {
                ...base,
                androidTableServerEnabled: base.androidTableServerEnabled === true,
                androidHostEnabled,
                androidHostPort: Number.isFinite(androidHostPort) && androidHostPort > 0 ? androidHostPort : 3500,
                ...(legacyHost ? { mainPCName: legacyHost } : {})
            };
        } catch (e) {
            console.warn('[Storage] Failed to get Android settings:', e);
            return {
                androidTableServerEnabled: false,
                androidHostEnabled: false,
                androidHostPort: 3500
            };
        }
    },

    saveAndroidSettings: async (settings) => {
        try {
            if (!settings || typeof settings !== 'object') {
                console.warn('[Storage] Invalid Android settings provided');
                return false;
            }
            const current = await Storage.getAndroidSettings();
            const merged = {
                ...current,
                ...settings
            };
            if (merged.androidHostPort !== undefined) {
                const parsedPort = Number(merged.androidHostPort);
                merged.androidHostPort = Number.isFinite(parsedPort) && parsedPort > 0 ? parsedPort : 3500;
            }
            await setValue(ANDROID_SETTINGS, merged);
            return true;
        } catch (e) {
            console.error('[Storage] Failed to save Android settings:', e);
            return false;
        }
    },

    setHasLoyalty: async (payload) => {
        setValue(HAS_LOYALTY, payload)
        store.commit('setHasLoyalty', payload);
    },
    getHasLoyalty: async () => {
        let value = store.getters.getHasLoyalty;
        if (value) return value;
        value = await getValue(HAS_LOYALTY);
        return value;
    },

    setZedMobileStatus: async (payload) => {
        setValue(ZED_MOBILE_STATUS, payload)
        store.commit('setZedMobileStatus', payload);
    },
    getZedMobileStatus: async () => {
        let value = store.getters.getZedMobileStatus;
        if (value) return value;
        value = await getValue(ZED_MOBILE_STATUS);
        return value;
    },

    setStoreCurrencies: async (payload) => {
        setValue(STORE_CURRENCIES, payload)
        store.commit('setStoreCurrencies', payload);
    },
    getStoreCurrencies: async () => {
        let value = store.getters.getStoreCurrencies;
        if (value) return value;
        value = await getValue(STORE_CURRENCIES);
        return value;
    },

    waitForHydrate: async () => {
        if (!store.getters.isHydrated) {
            await new Promise((resolve) => {
                const interval = setInterval(() => {
                    if (store.getters.isHydrated) {
                        clearInterval(interval);
                        resolve();
                    }
                }, 50);
            });
        }
    },

    setSupervisor: async (supervisor) => {
        await setValue(SUPERVISOR, supervisor);
        store.commit('setSupervisor', supervisor);
    },

    getSupervisor: async () => {
        let value = store.getters.getSupervisor;
        if (value) return value;
        value = await getValue(SUPERVISOR);
        return value;
    },

    // Offline Customers helpers
    addOfflineCustomer: async (customer) => {
        try {
            const list = (await getValue(OFFLINE_CUSTOMERS)) || [];
            list.push(customer);
            await setValue(OFFLINE_CUSTOMERS, list);
            return true;
        } catch (e) {
            console.warn('Failed to add offline customer:', e);
            return false;
        }
    },
    getOfflineCustomers: async () => {
        try {
            return (await getValue(OFFLINE_CUSTOMERS)) || [];
        } catch (e) {
            return [];
        }
    },
    setOfflineCustomers: async (list) => {
        try {
            await setValue(OFFLINE_CUSTOMERS, list || []);
            return true;
        } catch (e) {
            console.warn('Failed to set offline customers list:', e);
            return false;
        }
    },
    getCustomerIdMap: async () => {
        try {
            return (await getValue(CUSTOMER_ID_MAP)) || {};
        } catch (e) {
            return {};
        }
    },
    setCustomerIdMap: async (map) => {
        try {
            await setValue(CUSTOMER_ID_MAP, map || {});
            return true;
        } catch (e) {
            console.warn('Failed to set customer id map:', e);
            return false;
        }
    },
    updateCustomerIdMap: async (tempId, realId) => {
        try {
            const map = (await Storage.getCustomerIdMap()) || {};
            map[tempId] = realId;
            await setValue(CUSTOMER_ID_MAP, map);
            return map;
        } catch (e) {
            console.warn('Failed to update customer id map:', e);
            return null;
        }
    },

    // Lodging Integration helpers
    setLodgingApiKey: async (apiKey) => {
        await setValue(LODGING_API_KEY, apiKey);
    },
    getLodgingApiKey: async () => {
        return await getValue(LODGING_API_KEY);
    },
    setLodgingTenantId: async (tenantId) => {
        await setValue(LODGING_TENANT_ID, tenantId);
    },
    getLodgingTenantId: async () => {
        return await getValue(LODGING_TENANT_ID);
    },
    setLodgingName: async (name) => {
        await setValue(LODGING_NAME, name);
    },
    getLodgingName: async () => {
        return await getValue(LODGING_NAME);
    },

    // Enhanced Stores (with productId from subscription manager)
    setEnhancedStores: async (stores) => {
        await setValue(ENHANCED_STORES, stores);
    },
    getEnhancedStores: async () => {
        return await getValue(ENHANCED_STORES);
    },
    getEnhancedStoreByDatabase: async (database) => {
        const stores = await getValue(ENHANCED_STORES);
        if (!stores || !Array.isArray(stores)) return null;
        return stores.find(s => s.database === database);
    },
}
